/**
 * Карточка дашборда: оболочка, загрузка и состояния.
 *
 * Состояний у карточки четыре, и три из них — не результат: ждём, не хватает
 * слота, нет доступа. Ни одно из них не имеет права выглядеть как ноль.
 */

import { useEffect, useState } from "react";

import { ApiError, fetchPanel } from "../api/client";
import type { PanelResponse } from "../api/types";
import { useConflicts } from "../state/conflicts";
import { useStore } from "../state/store";
import { Disclosures } from "./Disclosures";
import { PanelBody } from "./PanelBody";
import type { ToolSpec } from "./tools";
import { VideoBasis } from "./VideoBasis";

export function Panel({ spec }: { spec: ToolSpec }) {
  const { state } = useStore();
  const { report } = useConflicts();
  const [response, setResponse] = useState<PanelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchPanel(spec.key, state, controller.signal)
      .then((data) => {
        setResponse(data);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof ApiError ? cause.message : "не удалось загрузить");
        setLoading(false);
      });
    return () => controller.abort();
  }, [spec.key, state]);

  // Конфликт имени общий для всех карточек, поэтому спрашивают о нём один раз
  // сверху. Карточка о нём сообщает, но сама вопрос не задаёт.
  const conflicts = response?.status === "needs_input" ? (response.conflicts ?? []) : [];
  useEffect(() => {
    report(spec.key, conflicts);
  }, [report, spec.key, conflicts]);

  return (
    <section className={`panel${spec.wide ? " panel--wide" : ""}`}>
      <header className="panel__head">
        <div className="panel__heading">
          <h2 className="panel__title">{spec.title}</h2>
          <p className="panel__hint">{spec.hint}</p>
        </div>
        {response?.status === "ok" && (
          // Базис подписан на каждой карточке отдельно: один период значит
          // для них разное, и без подписи два верных числа выглядят как
          // противоречие.
          <span className={`chip chip--${response.result.basis}`}>
            {response.result.basis_label}
          </span>
        )}
      </header>

      <div className="panel__body">
        {loading && <div className="skeleton" />}
        {!loading && error && <p className="error">{error}</p>}
        {!loading && !error && response?.status === "forbidden" && (
          <p className="denied">{response.message}</p>
        )}
        {!loading && !error && response?.status === "needs_input" && (
          <div className="needs">
            {conflicts.length > 0 ? (
              <p className="needs__text muted">Уточните имя в вопросе выше.</p>
            ) : (
              <p className="needs__text">{response.missing?.message}</p>
            )}
          </div>
        )}
        {!loading && !error && response?.status === "ok" && (
          <>
            <PanelBody kind={spec.kind} result={response.result} />
            <Disclosures items={response.result.disclosures} />
            <VideoBasis result={response.result} />
          </>
        )}
      </div>
    </section>
  );
}
